/**
 * <TutorNewDateStep> — la nouvelle date d'un individuel qu'on déplace (organism).
 *
 * L'étape `new_date` de l'ancien `ManageRDVScreen`, ses deux formes :
 *  - **cette séance seulement** (`one_occurrence`) — « Déplacer cette séance au : » : le jour et
 *    l'heure (`<DatePicker withTime>`), à partir d'aujourd'hui ;
 *  - **toutes les séances du créneau** (`all_occurrences`) — « Nouveau créneau régulier » : le jour
 *    de la semaine en pilules, puis l'heure, de demi-heure en demi-heure.
 * `hasNewDate` dit au gabarit quand « Continuer » s'allume ; `newDateLabel` écrit le choix tel que
 * l'étape de la raison le rappelle (« Nouveau RDV »).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { DatePicker } from "../../molecules/DatePicker"
import { SelectField } from "../../molecules/SelectField"
import { formatDayTime, formatDuration, formatHour } from "../../../ports/primary/ui/formatters/dateFormatter"

/** Déplacer une séance, ou le créneau régulier. */
export type TutorMoveKind = "one_occurrence" | "all_occurrences"

/** Le nouveau créneau régulier : le jour (0 = lundi) et l'heure (« 17:30 »). */
export type TutorNewSlot = { weekday: number | null; time: string | null }

const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"]

/** Les heures d'un créneau régulier, de demi-heure en demi-heure, de 8h00 à 21h30 (« 17:30 »). */
const TIMES = Array.from({ length: 28 }, (_, i) => {
    const minutes = 8 * 60 + i * 30
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${minutes % 60 === 0 ? "00" : "30"}`
})

/** « 17h30 » — l'heure d'un créneau, écrite comme toutes les heures du design system. */
const timeLabel = (time: string) => formatHour(`2000-01-01T${time}`)

/** La date (ou le créneau) est choisie : « Continuer » s'allume. */
export function hasNewDate(moveKind: TutorMoveKind, newDate: string, newSlot: TutorNewSlot): boolean {
    return moveKind === "one_occurrence" ? newDate !== "" : newSlot.weekday != null && newSlot.time != null
}

/** « Vendredi 30 octobre · 18h00 · 1h », « Jeudi à 17h30 · 1h » ; rien de choisi : « — ». */
export function newDateLabel(
    moveKind: TutorMoveKind,
    newDate: string,
    newSlot: TutorNewSlot,
    duration: number
): string {
    if (!hasNewDate(moveKind, newDate, newSlot)) return "—"
    return moveKind === "one_occurrence"
        ? `${formatDayTime(newDate.replace(" ", "T"))} · ${formatDuration(duration)}`
        : `${WEEKDAYS[newSlot.weekday ?? 0]} à ${timeLabel(newSlot.time ?? "")} · ${formatDuration(duration)}`
}

export type TutorNewDateStepProps = {
    moveKind: TutorMoveKind
    /** `YYYY-MM-DD HH:mm` — la nouvelle date d'une séance. */
    newDate: string
    onChangeNewDate?: (value: string) => void
    newSlot: TutorNewSlot
    onChangeNewSlot?: (slot: TutorNewSlot) => void
    testID?: string
}

export function TutorNewDateStep(props: TutorNewDateStepProps) {
    const { moveKind, newDate, onChangeNewDate, newSlot, onChangeNewSlot, testID } = props

    if (moveKind === "one_occurrence") {
        return (
            <DatePicker
                label="Nouvelle date"
                value={newDate}
                onChange={(value) => onChangeNewDate?.(value)}
                withTime
                minDate={new Date()}
                testID={testID}
            />
        )
    }

    return (
        <Stack gap="4" testID={testID}>
            <Row gap="2" style={styles.wrap}>
                {WEEKDAYS.map((day, index) => (
                    <ChoiceChip
                        key={day}
                        variant="pill"
                        label={day}
                        selected={newSlot.weekday === index}
                        onPress={() => onChangeNewSlot?.({ ...newSlot, weekday: index })}
                    />
                ))}
            </Row>
            <SelectField
                label="Heure"
                placeholder="Choisis l'heure"
                items={TIMES}
                getItemId={(time) => time}
                getItemLabel={timeLabel}
                selectedIds={newSlot.time != null ? [newSlot.time] : []}
                onChange={([time]) => onChangeNewSlot?.({ ...newSlot, time: time != null ? String(time) : null })}
            />
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    wrap: { flexWrap: "wrap" },
}))
